import { DeviceErrorType } from 'src/app/models/device-manger';

const MOS_THRESHOLD = 3.5;
const PACKET_LOSS_THRESHOLD = 5;
const RTT_THRESHOLD = 250;
const JITTER_THRESHOLD = 200;

export const HB_PC_ICE_SERVER = 'vm.pencilapp.com';
export const FETCH_VIDEO_CALL_STATS_INTERVAL = 10000;

export enum TrafficDirection {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
}

export enum NetworkMetricsThresholds {
  PACKET_LOSS = 'packet_loss',
  JITTER = 'jitter',
  RTT = 'rtt',
  MOS = 'MOS',
}

const defaultMetricsThresholds = {
  packet_loss: PACKET_LOSS_THRESHOLD,
  jitter: JITTER_THRESHOLD,
  rtt: RTT_THRESHOLD,
  MOS: MOS_THRESHOLD,
};

export const defaultInboundThresholds = {
  inbound_thresholds: defaultMetricsThresholds,
};

export const defaultOutboundThresholds = {
  outbound_thresholds: defaultMetricsThresholds,
};

export const defaultMediaDeviceState = {
  hasError: false,
  errorType: undefined,
};

export interface ConnectionInfo {
  id: string;
  pc: RTCPeerConnection;
}

export interface StatsReportItem {
  id: string;
  stats: WebRTCStatsParsed;
}

export interface NetworkStatsReportScore {
  inbound?: NetworkQualityParams;
  outbound?: NetworkQualityParams;
}

export interface LocalNetworkStatsScore {
  networkStatsScore: NetworkStatsReportScore;
  qualityLimitationDurations?: {
    other: number;
    cpu: number;
    bandwidth: number;
    none: number;
  };
}

export interface NetworkQualityParams {
  packetsLoss: number;
  avgJitter: number;
  rtt: number;
}

export interface UserSystemStatus {
  networkStatsScore?: NetworkStatsReportScore;
  devicesState?: DevicesState;
  isDeviceStruggling?: boolean;
}

export interface MediaDeviceState {
  hasError: boolean;
  errorType?: DeviceErrorType;
}

export interface DevicesState {
  audio?: MediaDeviceState;
  video?: MediaDeviceState;
}

export interface WebRTCStatsParsed {
  audio: {
    inbound: ParsedInboundAudioStreamStats[];
    outbound: ParsedOutboundAudioStreamStats[];
  };
  video: {
    inbound: ParsedInboundVideoStreamStats[];
    outbound: ParsedOutboundVideoStreamStats[];
  };
  connection: ParsedConnectionStats;
  remote: RemoteParsedStats;
}

export interface ParsedInboundAudioStreamStats {
  audioLevel: number;
  bitrate: number;
  bytesReceived: number;
  clockRate: number;
  codecId: string;
  concealedSamples: number;
  concealmentEvents: number;
  fecPacketsDiscarded: number;
  fecPacketsReceived: number;
  headerBytesReceived: number;
  id: string;
  insertedSamplesForDeceleration: number;
  jitter: number;
  jitterBufferDelay: number;
  jitterBufferEmittedCount: number;
  kind: 'audio';
  lastPacketReceivedTimestamp: number;
  mediaType: string;
  mimeType: string;
  packetRate: number;
  packetsDiscarded: number;
  packetsLost: number;
  packetsReceived: number;
  payloadType: number;
  remoteId: string;
  removedSamplesForAcceleration: number;
  silentConcealedSamples: number;
  ssrc: number;
  timestamp: number;
  totalAudioEnergy: number;
  totalSamplesDuration: number;
  totalSamplesReceived: number;
  track: {
    audioLevel: number;
    concealedSamples: number;
    concealmentEvents: number;
    detached: boolean;
    ended: boolean;
    id: string;
    insertedSamplesForDeceleration: number;
    jitterBufferDelay: number;
    jitterBufferEmittedCount: number;
    kind: 'audio';
    remoteSource: boolean;
    removedSamplesForAcceleration: number;
    silentConcealedSamples: number;
    timestamp: number;
    totalAudioEnergy: number;
    totalSamplesDuration: number;
    totalSamplesReceived: number;
    trackIdentifier: string;
    type: string;
  };
  trackId: string;
  transportId: string;
}

export interface ParsedOutboundAudioStreamStats {
  bitrate: number;
  bytesSent: number;
  clockRate: number;
  codecId: string;
  headerBytesSent: number;
  id: string;
  kind: string;
  mediaSourceId: string;
  mediaType: string;
  mimeType: string;
  nackCount: number;
  packetRate: number;
  packetsSent: number;
  payloadType: number;
  remoteId: string;
  retransmittedBytesSent: number;
  retransmittedPacketsSent: number;
  ssrc: number;
  timestamp: number;
  targetBitrate: number;
  track: {
    audioLevel: number;
    echoReturnLoss: number;
    echoReturnLossEnhancement: number;
    id: string;
    kind: string;
    timestamp: number;
    totalAudioEnergy: number;
    totalSamplesDuration: number;
    trackIdentifier: string;
    type: string;
  };
  trackId: string;
  transportId: string;
}

export interface ParsedInboundVideoStreamStats {
  bitrate: number;
  bytesReceived: number;
  clockRate: number;
  codecId: string;
  decoderImplementation: string;
  firCount: number;
  frameHeight: number;
  frameWidth: number;
  framesDecoded: number;
  framesDropped: number;
  framesPerSecond: number;
  framesReceived: number;
  headerBytesReceived: number;
  id: string;
  jitter: number;
  jitterBufferDelay: number;
  jitterBufferEmittedCount: number;
  keyFramesDecoded: number;
  kind: 'video';
  mediaType: string;
  mimeType: string;
  nackCount: number;
  packetRate: number;
  packetsLost: number;
  packetsReceived: number;
  payloadType: number;
  pliCount: number;
  ssrc: number;
  timestamp: number;
  totalDecodeTime: number;
  totalInterFrameDelay: number;
  totalSquaredInterFrameDelay: number;
  track: {
    detached: boolean;
    ended: boolean;
    frameHeight: number;
    frameWidth: number;
    framesDecoded: number;
    framesDropped: number;
    framesReceived: number;
    id: string;
    jitterBufferDelay: number;
    jitterBufferEmittedCount: number;
    kind: 'video';
    remoteSource: boolean;
    timestamp: number;
    trackIdentifier: string;
    type: string;
  };
  trackId: string;
  transportId: string;
}

export interface ParsedOutboundVideoStreamStats {
  bitrate: number;
  bytesSent: number;
  clockRate: number;
  codecId: string;
  encoderImplementation: string;
  firCount: number;
  frameHeight: number;
  frameWidth: number;
  framesEncoded: number;
  framesPerSecond: number;
  framesSent: number;
  headerBytesSent: number;
  hugeFramesSent: number;
  id: string;
  keyFramesEncoded: number;
  kind: string;
  mediaSourceId: string;
  mediaType: string;
  mimeType: string;
  nackCount: number;
  packetRate: number;
  packetsSent: number;
  payloadType: number;
  pliCount: number;
  qpSum: number;
  qualityLimitationDurations: {
    other: number;
    cpu: number;
    bandwidth: number;
    none: number;
  };
  qualityLimitationReason: 'none' | 'bandwidth' | 'cpu' | 'other';
  qualityLimitationResolutionChanges: number;
  remoteId: string;
  retransmittedBytesSent: number;
  retransmittedPacketsSent: number;
  rid: string;
  ssrc: number;
  timestamp: number;
  totalEncodeTime: number;
  totalEncodedBytesTarget: number;
  totalPacketSendDelay: number;
  track: {
    id: string;
    frames: number;
    framesPerSecond: number;
    height: number;
    timestamp: number;
    type: string;
    trackIdentifier: string;
    kind: string;
    width: number;
  };
  trackId: string;
  transportId: string;
  type: string;
}

export interface ParsedConnectionStats {
  availableOutgoingBitrate: number;
  bytesReceived: number;
  bytesSent: number;
  currentRoundTripTime: number;
  id: string;
  transportId: string;
  packetsReceived: number;
  packetsSent: number;
  state: string;
  totalRoundTripTime: number;
  type: string;
  local: IceCandidateConnectionStats;
  remote: IceCandidateConnectionStats;
}

export interface IceCandidateConnectionStats {
  address: string;
  candidateType: string;
  id: string;
  ip: string;
  isRemote: boolean;
  networkType?: string;
  port: number;
  priority: number;
  protocol: 'udp' | 'tcp';
  timestamp: number;
  transportId: string;
  type: 'local-candidate' | 'remote-candidate';
}

export interface RemoteParsedStats {
  video: {
    inbound: ParsedRemoteInboundStreamStats[];
    outbound: ParsedRemoteOutboundStreamStats[];
  };
  audio: {
    inbound: ParsedRemoteInboundStreamStats[];
    outbound: ParsedRemoteOutboundStreamStats[];
  };
}

export interface ParsedRemoteInboundStreamStats {
  clockRate: number;
  codecId: string;
  fractionLost: number;
  id: string;
  jitter: number;
  kind: string;
  localId: string;
  mimeType: string;
  packetsLost: number;
  payloadType: number;
  roundTripTime: number;
  roundTripTimeMeasurements: number;
  ssrc: number;
  timestamp: number;
  totalRoundTripTime: number;
  transportId: string;
  type: string;
}

export interface ParsedRemoteOutboundStreamStats {
  bytesSent: number;
  clockRate: number;
  codecId: string;
  id: string;
  kind: string;
  localId: string;
  mimeType: string;
  packetsSent: number;
  payloadType: number;
  remoteTimestamp: number;
  reportsSent: number;
  roundTripTimeMeasurements: number;
  ssrc: number;
  timestamp: number;
  totalRoundTripTime: number;
  transportId: string;
  type: string;
}

export enum StatsField {
  CANDIDATE_PAIR = 'candidate-pair',
  INBOUND_RTP = 'inbound-rtp',
  OUTBOUND_RTP = 'outbound-rtp',
  REMOTE_INBOUND_RTP = 'remote-inbound-rtp',
}
